import { Divider, Tab, Tabs } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiSideModal from "components/base/UiSideModal";
import UiTypography from "components/base/UiTypography";
import MultiSelectExtension from "components/Shared/MultiSelectExtension/MultiSelectExtension";
import CrewOptions from "modules/crews/CrewOptions";
import Create from "modules/OrganizationCrews/Create";
import { CreateCrewInput } from "generated/graphql";
import useCreateCrew from "hooks/organization/useCreateCrew";
import { useCallback, useState } from "react";
import { draftColors } from "./draftDesignTokens";
import { drawerTabStyles } from "./drawerTabStyles";

type AddLaborDrawerProps = {
  open: boolean;
  onClose: () => void;
  onAdd: (ids: string[]) => Promise<void>;
};

export default function AddLaborDrawer({ open, onClose, onAdd }: AddLaborDrawerProps) {
  const [createCrew, { loading, isSuccess, getData }] = useCreateCrew();
  const [crewIds, setCrewIds] = useState<string[]>([]);
  const [crewId, setCrewId] = useState<string | null>(null);
  const [openTab, setOpenTab] = useState(0);

  const handleCreate = useCallback(
    async (values: CreateCrewInput) => {
      const formattedInput = {
        ...values,
        rate: Math.floor(values.rate * 100),
      };
      const result = await createCrew({ variables: { input: formattedInput } });
      const data = getData(result);
      if (isSuccess(result.data)) {
        await onAdd([data.id]);
        onClose();
      }
      return { success: isSuccess(result.data), data };
    },
    [createCrew, getData, isSuccess, onAdd, onClose]
  );

  const handleAdd = useCallback(async () => {
    if (crewIds.length > 0) {
      await onAdd(crewIds);
      setCrewIds([]);
      onClose();
    }
  }, [crewIds, onAdd, onClose]);

  return (
    <UiSideModal open={open} onClose={onClose} title="Add Labor" width="598px">
      <MDBox display="flex" flexDirection="column" height="100%">
        <MDBox px="24px" pt="8px">
          <Tabs value={openTab} onChange={(_, v) => setOpenTab(v)} sx={drawerTabStyles}>
            <Tab label="Select existing" />
            <Tab label="Create new" />
          </Tabs>
        </MDBox>

        <Divider sx={{ borderColor: draftColors.border, m: 0 }} />

        <MDBox p="24px" display="flex" flexDirection="column" gap="16px" flex={1}>
          {openTab === 0 && (
            <CrewOptions>
              {({ crewOptions, crewsById, loading: optionsLoading, search, pagination }) => (
                <MDBox>
                  <UiTypography uiVariant="heading-sm" sx={{ mb: "12px" }}>
                    Crew(s)
                  </UiTypography>
                  <MultiSelectExtension
                    tags={crewIds}
                    setTags={setCrewIds}
                    options={crewOptions}
                    onChange={(value) => setCrewId(value as string)}
                    loading={optionsLoading}
                    pagination={pagination}
                    search={search}
                    id={crewId}
                    label="Crew"
                    nameCheckFn={crewsById}
                  />
                  <MDBox display="flex" justifyContent="flex-end">
                    <UiButton
                      uiVariant="primary-blue"
                      onClick={handleAdd}
                      disabled={crewIds.length < 1}
                    >
                      Add selected
                    </UiButton>
                  </MDBox>
                </MDBox>
              )}
            </CrewOptions>
          )}

          {openTab === 1 && <Create onSubmit={handleCreate} loading={loading} />}
        </MDBox>
      </MDBox>
    </UiSideModal>
  );
}
